import { severityOf } from '@/entities/finding/rules';

import type { GraphItem, GraphNode, GraphQuestion, TemplateGraph } from './graph';
import type { RuntimeNode, RuntimeTree } from './instances';
import type {
    Answer,
    AnswerKey,
    AnswerSet,
    NodeId,
    PackageNodeItemOption,
    PackageQuestionOption,
    QuestionId,
    QuestionOptionId,
} from './types';
import { parseAmountToCents } from './money';
import { traverseNode } from './visibility';

/**
 * SweepPlanner — "Geen gebreken hier".
 *
 * Vult de open vragen van een knoop met het antwoord dat zegt dat er niets aan de
 * hand is. Omdat de wandeling stopt bij de eerste openstaande vraag, gebeurt dat
 * iteratief: invullen, opnieuw wandelen, tot de knoop compleet is of tot een vraag
 * die de sweep niet mag invullen. Daar stopt hij, en die vraag blijft open.
 *
 * De knop zegt "geen gebreken", dus hij beantwoordt alleen een vraag naar gebreken,
 * nooit een feit: een verzonnen bouwperiode, type woning of VvE is fout in een
 * rapport. Een vraag krijgt een antwoord van de sweep als (`sweepAnswerFor`):
 *
 * 1. de vragenlijst zijn schone optie bij naam noemt: het eerste van
 *    `CLEAN_OPTION_LABELS` dat bij de vraag voorkomt. Zonder zo'n label blijft de
 *    vraag open; de terugval van de import op optie 1 telt niet;
 * 2. dat antwoord op dit item zegt dat het onderdeel in orde is: het label zelf
 *    ("Geen gebreken", "Goed"), of het oordeel op de rand is Goed of Voldoende.
 *    "Nee", "Niet van toepassing" en "Niet aanwezig" zeggen dat niet uit zichzelf:
 *    bij "Is er een VvE?" of "Welke onderdelen zijn van toepassing?" is het een feit;
 * 3. de rand niets vastlegt: geen gebrek of aandachtspunt (de regel van
 *    `SeverityRule`), geen bedrag, en geen getal- of tekstvraag erachter.
 *
 * Een vraag die al in een eerder onderdeel staat, hoort daar: de sweep stopt
 * ervoor. De API rekent precies zo (`SweepPlanner`).
 *
 * Het legacy formaat kent geen vlag voor "dit is het schone antwoord" (`isok` is de
 * validatie van de editor). Alles wat de regel nodig heeft - labels, oordeel,
 * bedragen en vervolgitem - staat al in het pakket.
 */

/** De schone labels, in volgorde van voorkeur. De eerste die bij een vraag voorkomt, is zijn schone optie. */
export const CLEAN_OPTION_LABELS: readonly string[] = [
    'Geen gebreken',
    'Goed',
    'Nee',
    'Niet van toepassing',
    'Niet aanwezig',
];

const NORMALISED_CLEAN_LABELS = CLEAN_OPTION_LABELS.map(normaliseLabel);

/** De labels die zelf zeggen dat het onderdeel in orde is. */
const IN_ORDER_LABELS: ReadonlySet<string> = new Set(['Geen gebreken', 'Goed'].map(normaliseLabel));

/** De oordelen die zeggen dat het onderdeel in orde is: Goed en Voldoende. */
const IN_ORDER_RESULTS: ReadonlySet<number> = new Set([8, 6]);

/** Waarom de sweep dit antwoord mag geven: het label zegt het, of het oordeel op de rand. */
export type SweepReason = 'in_order_label' | 'in_order_rating';

export type SweepBlockerReason =
    | 'value_question'
    | 'multi_count'
    | 'no_options'
    /**
     * De vragenlijst noemt geen schoon antwoord dat hier zegt dat het onderdeel in
     * orde is en niets vastlegt: een feitelijke vraag, of een vraag waarvan het
     * schone antwoord hier een bevinding is.
     */
    | 'no_clean_option'
    | 'unknown_question'
    /** De vraag staat al in een eerder onderdeel en wordt daar beantwoord. */
    | 'asked_elsewhere';

/** Het antwoord dat de sweep op één item geeft, of waarom hij dit item open laat. */
export type SweepAnswer =
    | { readonly option: PackageQuestionOption; readonly reason: SweepReason }
    | { readonly blocked: SweepBlockerReason };

export interface SweepEntry {
    readonly questionKey: AnswerKey;
    readonly questionId: QuestionId;
    readonly questionLabel: string;
    readonly optionId: QuestionOptionId;
    readonly optionLabel: string;
    readonly reason: SweepReason;
    /** Klaar om weg te schrijven; `option_ids` met exact één id. */
    readonly answer: Answer;
}

export interface SweepBlocker {
    readonly questionKey: AnswerKey;
    readonly questionId: QuestionId | null;
    readonly questionLabel: string;
    readonly reason: SweepBlockerReason;
}

export interface SweepPlan {
    readonly nodeId: NodeId;
    /** De in te vullen antwoorden, in de volgorde waarin ze gezet moeten worden. */
    readonly entries: readonly SweepEntry[];
    /** De vraag waar de sweep op strandt; `null` als de knoop compleet wordt. */
    readonly blocked: SweepBlocker | null;
    /** Wordt de knoop na het toepassen volledig beantwoord? */
    readonly complete: boolean;
    /** De antwoordset ná toepassing — klaar om in de state te zetten. */
    readonly answers: AnswerSet;
}

/**
 * Stelt het sweep-plan voor één knoop op. Wijzigt de meegegeven antwoorden niet.
 *
 * `askedBefore` zijn de vragen die een eerder onderdeel al stelt
 * (`questionsAskedBefore`). Wat de sweep hier invult, verandert daar niets aan:
 * een eerder onderdeel bereikt die vragen niet, anders hoorden ze daar.
 */
export function planNodeSweep(
    graph: TemplateGraph,
    answers: AnswerSet,
    tree: RuntimeTree,
    nodeId: NodeId,
    askedBefore: ReadonlySet<AnswerKey> = new Set(),
): SweepPlan {
    const runtimeNode = tree.nodes.get(nodeId);

    if (!runtimeNode) {
        return { nodeId, entries: [], blocked: null, complete: false, answers };
    }

    if (runtimeNode.multiCountKey && !isMultiCountAnswered(answers, runtimeNode.multiCountKey)) {
        return {
            nodeId,
            entries: [],
            blocked: {
                questionKey: runtimeNode.multiCountKey,
                questionId: null,
                questionLabel: runtimeNode.graphNode.node.multi_question_label ?? runtimeNode.name,
                reason: 'multi_count',
            },
            complete: false,
            answers,
        };
    }

    const entries: SweepEntry[] = [];
    let working: Record<AnswerKey, Answer> | null = null;
    let current: AnswerSet = answers;
    let blocked: SweepBlocker | null = null;
    const limit = runtimeNode.graphNode.items.length + 1;

    for (let round = 0; round <= limit; round += 1) {
        const traversal = traverseNode(graph, current, runtimeNode.graphNode, runtimeNode.scope);

        if (traversal.haltedKey === null) {
            return { nodeId, entries, blocked: null, complete: true, answers: current };
        }

        const entry = askedBefore.has(traversal.haltedKey)
            ? askedElsewhere(graph, traversal.haltedKey, traversal.haltedQuestionId)
            : planEntry(graph, runtimeNode, traversal.haltedKey, traversal.haltedItemId);

        if (isBlocker(entry)) {
            blocked = entry;
            break;
        }

        entries.push(entry);
        working = working ?? { ...answers };
        working[entry.questionKey] = entry.answer;
        current = working;
    }

    return { nodeId, entries, blocked, complete: false, answers: current };
}

/** Past een plan toe en geeft een nieuwe antwoordset terug. */
export function applySweepPlan(answers: AnswerSet, plan: SweepPlan): AnswerSet {
    if (plan.entries.length === 0) {
        return answers;
    }

    const next: Record<AnswerKey, Answer> = { ...answers };

    for (const entry of plan.entries) {
        next[entry.questionKey] = entry.answer;
    }

    return next;
}

function askedElsewhere(
    graph: TemplateGraph,
    questionKey: AnswerKey,
    questionId: QuestionId | null,
): SweepBlocker {
    return {
        questionKey,
        questionId,
        questionLabel:
            questionId === null ? '' : (graph.questionsById.get(questionId)?.question.label ?? ''),
        reason: 'asked_elsewhere',
    };
}

function isBlocker(entry: SweepEntry | SweepBlocker): entry is SweepBlocker {
    return !('answer' in entry);
}

function planEntry(
    graph: TemplateGraph,
    runtimeNode: RuntimeNode,
    questionKey: AnswerKey,
    itemId: number | null,
): SweepEntry | SweepBlocker {
    const item = itemId === null ? undefined : runtimeNode.graphNode.itemsById.get(itemId);
    const question = item ? graph.questionsById.get(item.questionId) : undefined;

    if (!item || !question) {
        return {
            questionKey,
            questionId: item?.questionId ?? null,
            questionLabel: '',
            reason: 'unknown_question',
        };
    }

    const answer = sweepAnswerFor(graph, runtimeNode.graphNode, item);

    if ('blocked' in answer) {
        return {
            questionKey,
            questionId: question.id,
            questionLabel: question.question.label,
            reason: answer.blocked,
        };
    }

    return {
        questionKey,
        questionId: question.id,
        questionLabel: question.question.label,
        optionId: answer.option.id,
        optionLabel: answer.option.label,
        reason: answer.reason,
        answer: { option_ids: [answer.option.id] },
    };
}

/**
 * Het antwoord dat "Geen gebreken hier" op dit item geeft, volgens de drie regels
 * bovenaan dit bestand - of waarom de sweep de vraag hier open laat. Hangt alleen af
 * van de vragenlijst, niet van de antwoorden.
 */
export function sweepAnswerFor(
    graph: TemplateGraph,
    graphNode: GraphNode,
    item: GraphItem,
): SweepAnswer {
    const question = graph.questionsById.get(item.questionId);

    if (!question) {
        return { blocked: 'unknown_question' };
    }

    if (question.isValueType) {
        return { blocked: 'value_question' };
    }

    if (question.options.length === 0) {
        return { blocked: 'no_options' };
    }

    const clean = namedCleanOption(question);

    if (!clean) {
        return { blocked: 'no_clean_option' };
    }

    const edges = item.options.filter((edge) => edge.question_option_id === clean.id);

    if (
        edges.some((edge) => recordsSomething(edge) || opensValueQuestion(graph, graphNode, edge))
    ) {
        return { blocked: 'no_clean_option' };
    }

    if (IN_ORDER_LABELS.has(normaliseLabel(clean.label))) {
        return { option: clean, reason: 'in_order_label' };
    }

    if (
        edges.length > 0 &&
        edges.every((edge) => edge.result !== null && IN_ORDER_RESULTS.has(edge.result))
    ) {
        return { option: clean, reason: 'in_order_rating' };
    }

    return { blocked: 'no_clean_option' };
}

/** De schone optie die de vragenlijst bij naam noemt: het eerste label uit de voorkeurslijst. */
function namedCleanOption(question: GraphQuestion): PackageQuestionOption | null {
    for (const label of NORMALISED_CLEAN_LABELS) {
        const match = question.options.find((option) => normaliseLabel(option.label) === label);

        if (match) {
            return match;
        }
    }

    return null;
}

/** Een gebrek, een aandachtspunt of een bedrag: dan is het antwoord niet schoon. */
function recordsSomething(edge: PackageNodeItemOption): boolean {
    const costs = {
        direct: parseAmountToCents(edge.direct_cost),
        term: parseAmountToCents(edge.term_cost),
        improvement: parseAmountToCents(edge.improvement_cost),
    };

    return (
        severityOf(edge.result, costs) !== 'clean' ||
        costs.direct !== 0 ||
        costs.term !== 0 ||
        costs.improvement !== 0
    );
}

/** Een getal- of tekstvraag kan de sweep niet invullen: dat antwoord laat meer werk achter. */
function opensValueQuestion(
    graph: TemplateGraph,
    graphNode: GraphNode,
    edge: PackageNodeItemOption,
): boolean {
    const next =
        edge.next_item_id === null ? undefined : graphNode.itemsById.get(edge.next_item_id);

    return next !== undefined && graph.questionsById.get(next.questionId)?.isValueType === true;
}

/** Kleine-letters, zonder harde spaties en dubbele witruimte. */
function normaliseLabel(label: string): string {
    return label
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

function isMultiCountAnswered(answers: AnswerSet, key: AnswerKey): boolean {
    const value = answers[key]?.value_number;

    return value !== null && value !== undefined && Number.isFinite(value);
}
